import React from 'react'

/**
 * Límite de errores de render. Sin esto, una excepción al dibujar (un dato con otra
 * forma, un punto sin coordenadas) desmonta TODA la aplicación y deja la pantalla en
 * blanco. Con esto, sólo cae el bloque envuelto y el resto sigue funcionando.
 *
 * Uso: <ErrorBoundary clave={seccion} titulo="No se pudo mostrar esta sección">…</ErrorBoundary>
 * `clave`: al cambiar (p. ej. otra sección) el límite se reinicia solo.
 */
export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { error: null, clave: props.clave }
  }

  static getDerivedStateFromError(error) {
    return { error }
  }

  static getDerivedStateFromProps(props, state) {
    // Otra sección/pantalla: se descarta el error anterior.
    if (props.clave !== state.clave) return { error: null, clave: props.clave }
    return null
  }

  componentDidCatch(error, info) {
    // Queda en consola para diagnosticar; el usuario ve el aviso de abajo.
    console.error('[ErrorBoundary]', error, info?.componentStack)
  }

  reintentar = () => this.setState({ error: null })

  render() {
    const { error } = this.state
    if (!error) return this.props.children
    const { titulo = 'No se pudo mostrar este contenido' } = this.props
    return (
      <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-50/70 p-6 text-center shadow-card">
        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-600">
          <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-base font-black text-ink">{titulo}</h3>
        <p className="mx-auto mt-1 max-w-md text-sm text-ink-soft">
          El resto de la pantalla sigue funcionando. Probá volver a intentar; si se repite, avisá a soporte con la hora y la sección.
        </p>
        <button type="button" onClick={this.reintentar}
          className="mt-4 rounded-xl border-2 border-rose-300 bg-white px-4 py-2 text-sm font-bold text-rose-700 shadow-sm hover:bg-rose-50">
          Reintentar
        </button>
        {import.meta.env.DEV && (
          <pre className="mx-auto mt-4 max-w-2xl overflow-auto rounded-lg bg-white p-3 text-left text-[11px] text-rose-800">{String(error?.stack || error)}</pre>
        )}
      </div>
    )
  }
}
